"use client";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card, CardContent } from "@/components/ui/card";
import Star from "lucide-react/dist/esm/icons/star";
import MessageSquare from "lucide-react/dist/esm/icons/message-square";
import { formatDistanceToNow } from "date-fns";

interface Review {
  id: string;
  rating: number;
  content?: string | null;
  created_at: string;
  profiles?: {
    display_name?: string | null;
    username?: string | null;
    avatar_url?: string | null;
  } | null;
}

interface SellerReviewsTabProps {
  reviews: Review[];
  totalReviewCount?: number;
}

export function SellerReviewsTab({ reviews, totalReviewCount }: SellerReviewsTabProps) {
  if (!reviews.length) return null;

  return (
    <section>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-lg font-bold flex items-center gap-2">
          <MessageSquare className="h-5 w-5 text-primary" />
          Recent Reviews
        </h2>
        {(totalReviewCount || 0) > 6 && (
          <span className="text-xs text-muted-foreground font-mono">
            {totalReviewCount} total reviews
          </span>
        )}
      </div>
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
        {reviews.map((review) => (
          <Card key={review.id} className="bg-card border border-border rounded-xl">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Avatar className="h-7 w-7">
                  <AvatarImage src={review.profiles?.avatar_url ?? undefined} />
                  <AvatarFallback className="text-2xs bg-muted">
                    {review.profiles?.display_name?.charAt(0) || "?"}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium truncate">
                    {review.profiles?.display_name || review.profiles?.username || "Anonymous"}
                  </p>
                  <p className="text-2xs text-muted-foreground">
                    {formatDistanceToNow(new Date(review.created_at), { addSuffix: true })}
                  </p>
                </div>
                <div className="flex items-center gap-0.5 shrink-0">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`h-3 w-3 ${
                        i < review.rating
                          ? "fill-accent text-accent"
                          : "text-muted-foreground/30"
                      }`}
                    />
                  ))}
                  <span className="ml-1 text-2xs font-mono text-muted-foreground">{review.rating}</span>
                </div>
              </div>
              {review.content && (
                <p className="text-xs text-muted-foreground line-clamp-3 leading-relaxed">
                  "{review.content}"
                </p>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </section>
  );
}
